import type { LearnTopic } from '../types';

/** Deltas arrive signed (a short put's delta is negative); the probability read is the magnitude. */
const asPercent = (delta: number) => Math.round(Math.abs(delta) * 100);

export const deltaTopic: LearnTopic = {
  id: 'delta',
  tier: 'position',
  title: 'Delta as a rough probability',
  summary: 'A 0.15 short delta reads as about a 15% chance that strike finishes in the money.',
  body: [
    "Delta measures how much an option's price moves for a one-dollar move in the stock. For a short strike it doubles as a rough probability: a 0.15 delta option has roughly a 15% chance of finishing in the money.",
    "For an iron condor, the two short deltas add to a rough chance that one side or the other finishes in the money: two 0.15 shorts give about a 30% chance of a breach and about a 70% chance every leg expires worthless. That 70% is the chance of keeping the whole credit — the chance of finishing with some profit is higher, because the breakevens sit a credit's width beyond each short strike.",
    'Delta moves with the stock. A short that started at 0.15 climbs toward 0.50 as the price approaches it, which is why a watch-level side deserves a decision before it reaches red.',
  ],
  explain(ctx) {
    const { shortPut, shortCall, shortPutDelta, shortCallDelta } = ctx;
    if (shortPutDelta == null && shortCallDelta == null) {
      // The Risk view has no per-leg Greeks; the chain page fetches them, so send the reader there
      // rather than implying the numbers do not exist anywhere.
      return ['Live deltas are not shown here; open the chain page for this position to see them.'];
    }

    const sentences: string[] = [];
    const sideSentence = (side: 'put' | 'call', strike: number | null, delta: number) => {
      const percent = asPercent(delta);
      const where = strike != null ? ` at ${strike}` : '';
      return `Your short ${side}${where} is at about ${percent}% delta right now, roughly a ${percent}% chance of finishing in the money.`;
    };

    if (shortPutDelta != null) sentences.push(sideSentence('put', shortPut, shortPutDelta));
    if (shortCallDelta != null) sentences.push(sideSentence('call', shortCall, shortCallDelta));

    if (shortPutDelta != null && shortCallDelta != null) {
      const breach = asPercent(shortPutDelta) + asPercent(shortCallDelta);
      // Two deep-ITM shorts can sum past 100; "and -12% of finishing inside" is nonsense, so floor it.
      const inside = Math.max(0, 100 - breach);
      sentences.push(
        `Together that is about a ${breach}% chance of a breach and ${inside}% of finishing inside.`,
      );
    }
    return sentences;
  },
  appliesTo() {
    return true;
  },
};
